import React from 'react';
import {px, percent, vh} from '@packages/systems/core/utils/WebflowCssSubset';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {memoize} from '@packages/utilities/memo';
import {buildPaypalCheckoutForm} from './CommerceUtils';
import {getPaddedUserItemsAndTotals, UserItem} from './utils/OrderItemsUtils';
import {
  CHECKOUT_BINDING_ROOT_QUERY_PATH,
  CHECKOUT_STATES,
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_PAYPAL_CHECKOUT_FORM_CONTAINER,
  ORDER_TYPE,
  CHECKOUT_QUERY,
  PREVIEW_ITEMS_DEFAULT,
  PREVIEW_ITEMS_KEY,
} from '@packages/systems/commerce/constants';
import {
  SHARED_FORM_WRAPPER_CONSTRAINTS_DESCENDANTS,
  SHARED_FORM_CONSTRAINTS_DESCENDANTS,
  SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
  SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
} from '@packages/systems/core/constants/SharedConstraintRules';
import withAttribute from './withAttribute';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {
  withMeta,
  withSystem,
  ElementPreset,
  Plugins,
  Atom,
  ContextType,
} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  TListOfElement,
  TRecord,
  TEnum,
  TTypeReference,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useSite} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getArrayOrElse,
  getChildren,
  getInPojo,
  getNumber,
  getNumberOrElse,
  getRecord,
  getString,
  getStringOrElse,
} from '@packages/utilities/data-utils';
import {
  CurrencySettings,
  FormattedPrice,
} from '@packages/systems/core/utils/CurrencyUtils';

const memoizedGetUserItemsAndTotals = memoize(
  (
    previewItemsCount: number,
    userItems: UserItem[],
    extraItems: {price: FormattedPrice}[],
    currencySettings: CurrencySettings
  ) =>
    getPaddedUserItemsAndTotals(
      previewItemsCount,
      userItems,
      extraItems,
      currencySettings
    )
);

const modifyContextData = (
  data: EvaluatedNodeData,
  env: null | Environment,
  context: ContextType,
  contextData: unknown
) => {
  const previewItemsCount = getNumberOrElse(
    getInPojo(data, ['temp', PREVIEW_ITEMS_KEY]),
    PREVIEW_ITEMS_DEFAULT
  );
  const contextRecord = getRecord(contextData);
  const copy = contextRecord ? {...contextRecord} : {};
  copy['cardProvider'] = 'PayPal';
  copy['cardLastFour'] = null;
  copy['cardExpiresMonth'] = null;
  copy['cardExpiresYear'] = null;

  if (env === WebflowEnvironments.DESIGNER) {
    const mockRequiresShipping =
      getStringOrElse(
        getInPojo(data, ['temp', 'requiresShipping']),
        ORDER_TYPE.REQUIRE_SHIPPING
      ) === ORDER_TYPE.REQUIRE_SHIPPING;
    copy['requiresShipping'] = mockRequiresShipping;

    const currencySettings = context.get('ecommCurrencySettings');
    const toExtraItem = (
      item: unknown
    ): {price: FormattedPrice} | undefined => {
      const price = getRecord(getInPojo(item, ['price']));
      if (price) {
        const value = getNumber(getInPojo(price, ['value']));
        const unit = getString(getInPojo(price, ['unit']));
        const str = getString(getInPojo(price, ['string']));
        if (
          typeof value === 'number' &&
          typeof unit === 'string' &&
          typeof str === 'string'
        ) {
          return item as {price: FormattedPrice};
        } else {
          return undefined;
        }
      } else {
        return undefined;
      }
    };

    if (currencySettings && contextData) {
      const extraItems: {price: FormattedPrice}[] = getArrayOrElse(
        getInPojo(contextData, ['extraItems']),
        []
      )
        .map(toExtraItem)
        .filter((it) => it !== undefined);

      const userItems: UserItem[] = getArrayOrElse(
        getInPojo(contextData, ['userItems']),
        []
        // not happy with this, but there's no fast way to verify that the value is
        // a user item, and too much code depends on it.
      ).map((it) => it as UserItem);
      const {paddedUserItems, subtotal, total} = memoizedGetUserItemsAndTotals(
        previewItemsCount,
        userItems,
        extraItems,
        currencySettings
      );
      copy['userItems'] = paddedUserItems;
      copy['subtotal'] = subtotal;
      copy['total'] = total;
    }
  }

  return copy;
};

const CommercePaypalCheckoutFormContainer = Atom.createRender(
  ({attributes, classes, context, data, generateAttributes, renderChild}) => {
    const site = useSite();
    const env = useRuntimeEnvironment();

    const newContext = context
      .update('data', (contextData) => {
        if (env !== WebflowEnvironments.SSR) {
          return modifyContextData(data, env, context, contextData || {});
        }
        return contextData;
      })
      .set('bindingPathPrefix', CHECKOUT_BINDING_ROOT_QUERY_PATH)
      .set(
        'errorState',
        getString(getInPojo(data, ['temp', 'state'])) ?? CHECKOUT_STATES.DEFAULT
      )
      .set(
        'previewItemsCount',
        getNumberOrElse(
          getInPojo(data, ['temp', PREVIEW_ITEMS_KEY]),
          PREVIEW_ITEMS_DEFAULT
        )
      );

    const query = context.get('query') || '';
    const newAttributes = attributes
      .set(CHECKOUT_QUERY, query)
      .set('data-wf-page-link-href-prefix', site.get('pageLinkHrefPrefix', ''));

    return (
      <div {...generateAttributes(newAttributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, newContext)
        )}
      </div>
    );
  }
);

export const createCommercePaypalCheckoutFormContainer: ElementPreset.ExpressionEscapeHatchFactory =
  ({collectionState, betaFeatures}) => {
    if (collectionState === undefined) {
      throw Error(
        'collectionState is required to instantiate createCommercePaypalCheckoutFormContainer elementPreset'
      );
    }

    return buildPaypalCheckoutForm({
      collectionState,
      betaFeatures: betaFeatures || null,
    });
  };

const config = createIntegratedAtom({
  displayName: 'PayPal Checkout Form',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('FormBlock')),
  },
  showEmpty: true,
  constraints: {
    descendants: [
      ...SHARED_FORM_WRAPPER_CONSTRAINTS_DESCENDANTS,
      ...SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
      ...SHARED_FORM_CONSTRAINTS_DESCENDANTS,
      {is: 'CommerceCheckoutPlaceOrderButton', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutShippingMethodsWrapper', rule: 'ExactlyOne'},
      {is: 'CommercePaypalCheckoutErrorState', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutCustomerInfoWrapper', rule: 'Forbid'},
      {is: 'CommerceCheckoutPaymentInfoWrapper', rule: 'Forbid'},
      {is: 'CommerceCheckoutBillingAddressToggleWrapper', rule: 'Forbid'},
      {is: 'CommerceCheckoutShippingAddressWrapper', rule: 'Forbid'},
      {is: 'CommerceCheckoutBillingAddressWrapper', rule: 'Forbid'},
      {is: 'CommerceCheckoutDiscounts', rule: 'ZeroOrOne'},
    ],
    ancestors: SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
    page: [{type: 'UtilityPage', is: 'ecommerce-paypal-checkout'}],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          width: percent(100),
          minHeight: vh(100),
          paddingTop: px(20),
          paddingBottom: px(20),
          paddingRight: px(20),
          paddingLeft: px(20),
          backgroundColor: createRgbaColor('f5f5f5'),
        },
      },
    },
  },
  render: withAttribute(
    DATA_ATTR_NODE_TYPE,
    NODE_TYPE_COMMERCE_PAYPAL_CHECKOUT_FORM_CONTAINER
  )(CommercePaypalCheckoutFormContainer),
  fields: {
    children: TListOfElement,
    temp: TRecord(
      {
        requiresShipping: withSystem(
          {
            defaultValue: ORDER_TYPE.REQUIRE_SHIPPING,
            label: 'Order',
          },
          TEnum({
            [ORDER_TYPE.REQUIRE_SHIPPING]: 'Shipping',
            [ORDER_TYPE.NO_SHIPPING]: 'No Shipping',
          })
        ),
        state: withSystem(
          {
            label: 'State',
            defaultValue: 'DEFAULT',
          },
          TEnum({
            DEFAULT: 'Default',
            ERROR: 'Error',
          })
        ),
        previewItems: withSystem(
          {
            label: 'Preview',
            defaultValue: PREVIEW_ITEMS_DEFAULT,
          },
          TTypeConstructor(qualify(Plugins.name('Basic'), typeName('Slider')))
        ),
        note: withMeta(
          {
            content:
              'You can preview up to 20 order items. If you do not have enough products, sample items will be generated.',
          },
          withSystem(
            {defaultValue: false},
            TTypeConstructor(
              qualify(Plugins.name('Commerce'), typeName('DismissibleNote'))
            )
          )
        ),
      },
      TTypeReference('extraTempData')
    ),
  },
});

export default config;
